import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, HostBinding, inject, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild,
    ChangeDetectionStrategy
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { DebuggingBreakpoint } from "src/modules/enaio/netgen/DebuggingBreakpoint";
import { ErrorType } from "src/modules/enaio/netgen/ErrorType";
import { InFileError } from "src/modules/enaio/netgen/InFileError";
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from "src/modules/utils/shared/utils";
import { EditorCompletionProvider } from "../../models/editor-completion.model";
import { EditorSelection } from "../../models/editor-selection.model";
import { MonacoEditorLoaderService, MonacoModule } from "../../services/monaco-editor-loader.service";

type MonacoEditor = import("monaco-editor/esm/vs/editor/editor.api.js").editor.IStandaloneCodeEditor;
type MonacoDisposable = import("monaco-editor/esm/vs/editor/editor.api.js").IDisposable;
type MonacoEditorOptions = import("monaco-editor/esm/vs/editor/editor.api.js").editor.IStandaloneEditorConstructionOptions;
type MonacoModel = import("monaco-editor/esm/vs/editor/editor.api.js").editor.ITextModel;
type MonacoDecorations = import("monaco-editor/esm/vs/editor/editor.api.js").editor.IEditorDecorationsCollection;
type MonacoPosition = import("monaco-editor/esm/vs/editor/editor.api.js").IPosition;
type MonacoViewState = import("monaco-editor/esm/vs/editor/editor.api.js").editor.ICodeEditorViewState;
type MonacoRange = import("monaco-editor/esm/vs/editor/editor.api.js").IRange;
type EditorError = InFileError & {fixTitle?: string; fixOldText?: string; fixNewText?: string};
interface EditorQuickFix {
    line: number;
    column: number;
    message: string;
    title: string;
    oldText: string;
    newText: string;
}

@Component({
    selector: "app-monaco-editor",
    templateUrl: "./monaco-editor.component.html",
    styleUrl: "./monaco-editor.component.scss",
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => MonacoEditorComponent),
        multi: true
    }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MonacoEditorComponent implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

    private static logPatterns = new WeakMap<MonacoModel, RegExp>();
    private static logFoldingRegistered = false;
    private static validationOwner = "sm-validation";
    private static includedScriptMarker = "!!!!!!!!!!!!!!!!!!!! INKLUDIERTES SKRIPT";

    private loader = inject(MonacoEditorLoaderService);
    private zone = inject(NgZone);
    private monaco: MonacoModule;
    private editor: MonacoEditor;
    private listeners: MonacoDisposable[] = [];
    private lineHighlight: MonacoDecorations;
    private dividerDecorations: MonacoDecorations;
    private breakpointDecorations: MonacoDecorations;
    private errorGlyphDecorations: MonacoDecorations;
    private foldTimer: ReturnType<typeof setTimeout>;
    private validationTimer: ReturnType<typeof setTimeout>;
    private validationRequest = 0;
    private terminalHistory: string[] = [];
    private terminalHistoryIndex = -1;
    private pendingJump: {line: number; highlight: boolean; className: string};
    private pendingHighlight: {line: number; scrollIntoView: boolean; className: string};
    private pendingViewState: MonacoViewState;
    private dividerLines: number[] = [];
    private breakpointLines = new Set<number>();
    private quickFixes: EditorQuickFix[] = [];
    private destroyed = false;
    private disabled = false;
    private value = "";
    private onChange: (value: string) => void = () => null;
    private onTouched: () => void = () => null;

    @ViewChild("container", {static: true}) container: ElementRef<HTMLDivElement>;

    @Input()
    language = "plaintext";
    @Input()
    readOnly = false;
    @Input()
    theme: string;
    @Input()
    options: MonacoEditorOptions = {};
    @Input()
    lineBlockFoldingStartPattern: string;
    @Input()
    collapseLineBlocks = false;
    @Input()
    singleLine = false;
    @Input()
    asTerminal = false;
    @Input()
    validateCallback: (language: string, text: string) => Promise<InFileError[]>;
    @Input()
    allowBreakpoints = false;
    @Input()
    canSetBreakpoint: (line: number) => boolean;
    @Input()
    completionProvider: EditorCompletionProvider;
    @Output()
    textChange = new EventEmitter<string>();
    @Output()
    terminalCommand = new EventEmitter<string>();
    @Output()
    editorReady = new EventEmitter<void>();
    @Output()
    contextMenu = new EventEmitter<EditorSelection>();
    @Output()
    clickToken = new EventEmitter<EditorSelection>();
    @Output()
    breakpointAdded = new EventEmitter<number>();
    @Output()
    breakpointRemoved = new EventEmitter<number>();

    @HostBinding("class.single-line")
    get singleLineClass(): boolean {
        return this.singleLine;
    }

    @Input()
    set breakpoints(value: DebuggingBreakpoint[]) {
        this.breakpointLines = new Set((value ?? []).map(bp => bp.line).filter(line => this.canSetBreakpoint == null || this.canSetBreakpoint(line - 1)));
        this.applyBreakpoints();
    }

    get text(): string {
        return this.getValue();
    }

    async ngAfterViewInit(): Promise<void> {
        this.monaco = await this.loader.load(this.language);
        if (this.destroyed) {
            return;
        }

        this.registerLogFolding();

        this.zone.runOutsideAngular(() => {
            this.editor = this.monaco.editor.create(this.container.nativeElement, {
                automaticLayout: true,
                scrollBeyondLastLine: false,
                fontSize: 13,
                minimap: {enabled: false},
                // Schmaler linker Rand: Zeilennummern wachsen bei Bedarf über drei Stellen hinaus
                lineNumbersMinChars: 3,
                lineDecorationsWidth: 4,
                ...this.allowBreakpoints || this.validateCallback != null ? {glyphMargin: true} : {},
                // Mit eigenem Kontextmenü des Aufrufers wird das Monaco-Menü unterdrückt
                ...this.contextMenu.observed ? {contextmenu: false} : {},
                // Mit eigenem Provider sind nur dessen Vorschläge erwünscht, nicht die aus Wörtern des Dokuments
                ...this.completionProvider != null ? {wordBasedSuggestions: "off" as const, quickSuggestions: {other: true, strings: true, comments: false}} : {},
                ...this.options,
                language: this.loader.normalizeLanguage(this.language),
                readOnly: this.readOnly || this.disabled,
                value: this.value
            });
            this.configureLogFolding();
            this.configureTerminal();
            if (this.theme != null) {
                this.monaco.editor.setTheme(this.theme);
            }
            this.listeners.push(this.editor.onDidChangeModelContent(() => {
                let value = this.editor.getValue();
                if (this.asTerminal && /[\r\n]/.test(value)) {
                    this.editor.setValue(value.replace(/\r?\n|\r/g, ""));
                    return;
                }
                if (value != this.value) {
                    this.value = value;
                    this.zone.run(() => {
                        this.onChange(value);
                        this.textChange.emit(value);
                    });
                    this.scheduleValidation();
                }
            }));
            this.listeners.push(this.editor.onDidBlurEditorWidget(() => this.zone.run(() => this.onTouched())));
            this.listeners.push(this.editor.onMouseDown(event => this.onMouseDown(event)));
            this.listeners.push(this.editor.onContextMenu(event => this.onContextMenu(event)));
            this.listeners.push(this.monaco.editor.onDidChangeMarkers(uris => this.suppressLanguageMarkers(uris)));
            this.registerCompletionProvider();
            this.registerQuickFixProvider();
            this.applyDividers();
            this.applyBreakpoints();
            this.scheduleInitialFold();
            this.applyPendingJump();
            this.applyPendingViewState();
            this.scheduleValidation();
        });
        this.zone.run(() => this.editorReady.emit());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.language) {
            void this.updateLanguage();
        }
        if (changes.options && this.editor != null) {
            this.editor.updateOptions(this.options ?? {});
        }
        if (changes.theme && this.editor != null && this.theme != null) {
            this.monaco.editor.setTheme(this.theme);
        }
        if ((changes.readOnly || changes.options) && this.editor != null) {
            this.updateReadOnly();
        }
        if ((changes.lineBlockFoldingStartPattern || changes.collapseLineBlocks) && this.editor != null) {
            this.configureLogFolding();
            this.scheduleInitialFold();
        }
        if ((changes.allowBreakpoints || changes.validateCallback) && this.editor != null) {
            this.editor.updateOptions({glyphMargin: this.allowBreakpoints || this.validateCallback != null});
        }
        if (changes.readOnly || changes.validateCallback) {
            this.scheduleValidation();
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        for (let listener of this.listeners) {
            listener.dispose();
        }
        this.listeners = [];
        clearTimeout(this.foldTimer);
        clearTimeout(this.validationTimer);
        this.lineHighlight?.clear();
        this.dividerDecorations?.clear();
        this.breakpointDecorations?.clear();
        this.errorGlyphDecorations?.clear();
        if (this.editor?.getModel() != null) {
            MonacoEditorComponent.logPatterns.delete(this.editor.getModel());
        }
        this.editor?.dispose();
        this.editor = null;
        this.monaco = null;
    }

    writeValue(value: string): void {
        this.value = value ?? "";
        if (this.editor != null && this.editor.getValue() != this.value) {
            this.editor.setValue(this.value);
            this.highlightLine(null);
            this.applyDividers();
            this.applyBreakpoints();
            this.scheduleInitialFold();
            this.applyPendingJump();
            this.scheduleValidation();
        }
    }

    setText(value: string): void {
        this.writeValue(value);
        this.onChange(this.value);
    }

    registerOnChange(onChange: (value: string) => void): void {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: () => void): void {
        this.onTouched = onTouched;
    }

    setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
        this.updateReadOnly();
    }

    getValue(): string {
        return this.editor?.getValue() ?? this.value;
    }

    focus(): void {
        this.editor?.focus();
    }

    layout(): void {
        this.editor?.layout();
    }

    getEditor(): MonacoEditor {
        return this.editor;
    }

    getCursorPosition(): {row: number; column: number} {
        let position = this.editor?.getPosition();
        return position == null ? {row: 0, column: 0} : {row: position.lineNumber - 1, column: position.column - 1};
    }

    saveViewState(): MonacoViewState {
        return this.editor?.saveViewState() ?? null;
    }

    restoreViewState(state: MonacoViewState): void {
        if (state == null) {
            return;
        }
        if (this.editor == null) {
            this.pendingViewState = state;
            return;
        }
        this.editor.restoreViewState(state);
    }

    private applyPendingViewState(): void {
        if (this.pendingViewState != null) {
            this.editor.restoreViewState(this.pendingViewState);
            this.pendingViewState = null;
        }
    }

    jumpToLine(line: number, highlight = false, className = "monaco-custom-highlight"): boolean {
        let model = this.editor?.getModel();
        if (model == null || !Number.isInteger(line) || line < 1 || line > model.getLineCount()) {
            if (Number.isInteger(line) && line > 0) {
                this.pendingJump = {line, highlight, className};
            }
            return false;
        }

        this.pendingJump = null;
        this.editor.layout();
        this.editor.setPosition({lineNumber: line, column: 1});
        this.editor.revealLineInCenter(line);
        if (highlight) {
            this.highlightLine(line, false, className);
        }
        return true;
    }

    private applyPendingJump(): void {
        if (this.pendingJump != null) {
            this.jumpToLine(this.pendingJump.line, this.pendingJump.highlight, this.pendingJump.className);
        }
        if (this.pendingHighlight != null) {
            this.highlightLine(this.pendingHighlight.line, this.pendingHighlight.scrollIntoView, this.pendingHighlight.className);
        }
    }

    highlightLine(line: number | null, scrollIntoView = false, className = "monaco-custom-highlight"): void {
        this.lineHighlight?.clear();
        this.pendingHighlight = null;
        if (line == null) {
            return;
        }
        if (this.editor?.getModel() == null || line < 1 || line > this.editor.getModel().getLineCount()) {
            this.pendingHighlight = {line, scrollIntoView, className};
            return;
        }

        this.lineHighlight ??= this.editor.createDecorationsCollection();
        this.lineHighlight.set([{
            range: new this.monaco.Range(line, 1, line, 1),
            options: {isWholeLine: true, className}
        }]);
        if (scrollIntoView) {
            this.editor.revealLineInCenterIfOutsideViewport(line);
        }
    }

    // Die Zeilennummer ist nullbasiert; die Trennlinie wird über dieser Zeile angezeigt, null entfernt alle Trennlinien
    addDivider(lineNumber: number | null): void {
        this.dividerLines = lineNumber == null ? [] : [...this.dividerLines, lineNumber + 1];
        this.applyDividers();
    }

    private applyDividers(): void {
        let model = this.editor?.getModel();
        if (model == null) {
            return;
        }
        this.dividerDecorations ??= this.editor.createDecorationsCollection();
        this.dividerDecorations.set(this.dividerLines.filter(line => line <= model.getLineCount()).map(line => ({
            range: new this.monaco.Range(line, 1, line, 1),
            options: {isWholeLine: true, className: "monaco-line-divider"}
        })));
    }

    private applyBreakpoints(): void {
        let model = this.editor?.getModel();
        if (model == null) {
            return;
        }
        this.breakpointDecorations ??= this.editor.createDecorationsCollection();
        this.breakpointDecorations.set([...this.breakpointLines].filter(line => line <= model.getLineCount()).map(line => ({
            range: new this.monaco.Range(line, 1, line, 1),
            options: {isWholeLine: true, glyphMarginClassName: "monaco-breakpoint", lineNumberClassName: "monaco-breakpoint-line-number"}
        })));
    }

    private toggleBreakpoint(line: number): void {
        if (this.breakpointLines.has(line)) {
            this.breakpointLines.delete(line);
            this.applyBreakpoints();
            this.zone.run(() => this.breakpointRemoved.emit(line));
        }
        else if (this.canSetBreakpoint == null || this.canSetBreakpoint(line - 1)) {
            this.breakpointLines.add(line);
            this.applyBreakpoints();
            this.zone.run(() => this.breakpointAdded.emit(line));
        }
    }

    private onMouseDown(event: import("monaco-editor/esm/vs/editor/editor.api.js").editor.IEditorMouseEvent): void {
        let targetType = this.monaco.editor.MouseTargetType;
        let position = event.target.position;
        if (position == null) {
            return;
        }
        // Ein Klick auf das Randsymbol einer Zeile mit Korrektur setzt den Cursor an die markierte Stelle und öffnet die Schnellkorrektur
        let model = this.editor.getModel();
        let fix = event.event.leftButton && event.target.type == targetType.GUTTER_GLYPH_MARGIN && model != null ?
            this.quickFixes.find(f => f.line == position.lineNumber && this.getQuickFixRange(model, f) != null) : null;
        if (fix != null) {
            event.event.preventDefault();
            this.editor.setPosition({lineNumber: fix.line, column: fix.column});
            this.editor.focus();
            this.editor.trigger("glyphMargin", "editor.action.quickFix", null);
            return;
        }
        if (this.allowBreakpoints && event.event.leftButton && (event.target.type == targetType.GUTTER_GLYPH_MARGIN || event.target.type == targetType.GUTTER_LINE_NUMBERS)) {
            this.toggleBreakpoint(position.lineNumber);
            event.event.preventDefault();
            return;
        }
        if (event.event.ctrlKey && event.event.leftButton && event.target.type == targetType.CONTENT_TEXT && this.clickToken.observed) {
            let selection = this.createSelection(position);
            if (!Utils.isNoe(selection.token)) {
                this.zone.run(() => this.clickToken.emit(selection));
            }
        }
    }

    private onContextMenu(event: import("monaco-editor/esm/vs/editor/editor.api.js").editor.IEditorMouseEvent): void {
        if (!this.contextMenu.observed) {
            return;
        }
        event.event.preventDefault();
        let position = event.target.position ?? this.editor.getPosition();
        if (position != null) {
            this.zone.run(() => this.contextMenu.emit(this.createSelection(position)));
        }
    }

    private createSelection(position: MonacoPosition): EditorSelection {
        let model = this.editor.getModel();
        let word = model.getWordAtPosition(position)?.word ?? "";
        let selection = this.editor.getSelection();
        let selectedText = selection == null || selection.isEmpty() ? "" : model.getValueInRange(selection);
        return new EditorSelection(position.lineNumber - 1, position.column - 1, word, selectedText);
    }

    async validate(text = this.getValue()): Promise<InFileError[]> {
        if (this.validateCallback == null) {
            return [];
        }
        return this.validateCallback(this.language, Strings.getSubstringTo(text, MonacoEditorComponent.includedScriptMarker));
    }

    private scheduleValidation(): void {
        let request = ++this.validationRequest;
        clearTimeout(this.validationTimer);
        if (this.editor == null || this.readOnly || this.validateCallback == null) {
            return;
        }
        if (this.getValue() == "") {
            this.setErrors([]);
            return;
        }
        this.validationTimer = Utils.setTimerOnce(400, async () => {
            let errors = await this.validate();
            if (request == this.validationRequest) {
                this.setErrors(errors);
            }
            this.validationTimer = null;
        });
    }

    setErrors(errors: EditorError[]): void {
        let model = this.editor?.getModel();
        if (model == null) {
            return;
        }
        let lineCount = model.getLineCount();
        let quickFixes: EditorQuickFix[] = [];
        let markers = (errors ?? []).map(error => {
            let line = Math.min(Math.max(1, error.lineNumber), lineCount);
            let startColumn = Math.min(Math.max(1, error.columnNumber), model.getLineMaxColumn(line));
            let message = Strings.def(error.shortMessage, error.message);
            if (error.fixOldText != null && error.fixNewText != null) {
                quickFixes.push({line, column: startColumn, message, title: Strings.def(error.fixTitle, "Korrektur übernehmen"), oldText: error.fixOldText, newText: error.fixNewText});
            }
            return {
                startLineNumber: line,
                startColumn,
                endLineNumber: line,
                endColumn: Math.max(startColumn + 1, model.getLineMaxColumn(line)),
                severity: error.errorType == ErrorType.error ? this.monaco.MarkerSeverity.Error : this.monaco.MarkerSeverity.Warning,
                message,
                ...error.errorKind == "ai" ? {source: "KI"} : {}
            };
        });
        this.quickFixes = quickFixes;
        this.monaco.editor.setModelMarkers(model, MonacoEditorComponent.validationOwner, markers);

        // Jede betroffene Zeile erhält ein Symbol am linken Rand mit allen Meldungen als Tooltip
        let lines = [...new Set(markers.map(marker => marker.startLineNumber))];
        this.errorGlyphDecorations ??= this.editor.createDecorationsCollection();
        this.errorGlyphDecorations.set(lines.map(line => {
            let lineMarkers = markers.filter(marker => marker.startLineNumber == line);
            let isError = lineMarkers.some(marker => marker.severity == this.monaco.MarkerSeverity.Error);
            let isAi = lineMarkers.every(marker => marker.source == "KI");
            // Nur Korrekturen anbieten, deren alter Text noch unverändert an der gemeldeten Stelle steht
            let lineFixes = quickFixes.filter(fix => fix.line == line && this.getQuickFixRange(model, fix) != null);
            return {
                range: new this.monaco.Range(line, 1, line, 1),
                options: {
                    glyphMarginClassName: "codicon " + (isAi ? "codicon-sparkle monaco-ai-glyph" : isError ? "codicon-error monaco-error-glyph" : "codicon-warning monaco-warning-glyph"),
                    glyphMarginHoverMessage: [...lineMarkers.map(marker => ({value: this.toGlyphHoverMarkdown((marker.source == "KI" && !marker.message.startsWith("KI: ") ? "KI: " : "") + marker.message)})),
                        ...lineFixes.map(fix => ({value: "Korrektur: " + this.toInlineCode(fix.oldText) + " → " + this.toInlineCode(fix.newText) +
                            " – zum Übernehmen auf das Symbol klicken oder den Cursor an die markierte Stelle setzen und Strg+. drücken"}))]
                }
            };
        }));
    }

    // Der Rand-Tooltip ist Markdown und fasst Zeilenumbrüche zusammen; mehrzeilige Meldungen wie Quelltextvorschauen bleiben als Codeblock formatiert
    private toGlyphHoverMarkdown(message: string): string {
        if (!message.includes("\n")) {
            return message;
        }
        let fence = "```";
        while (message.includes(fence)) {
            fence += "`";
        }
        return fence + "plaintext\n" + message + "\n" + fence;
    }

    // Inline-Code in Markdown: die Begrenzung ist länger als jede Backtick-Folge im Text, Zeilenumbrüche werden als ⏎ angezeigt
    private toInlineCode(text: string): string {
        let value = text.replace(/\r?\n/g, " ⏎ ");
        if (value == "") {
            return "(leer)";
        }
        let fence = "`";
        while (value.includes(fence)) {
            fence += "`";
        }
        return fence + " " + value + " " + fence;
    }

    // Korrekturen der Validierung werden als Monaco-Schnellkorrektur angeboten und ersetzen nur, solange der alte Text noch unverändert dasteht
    // Für alle Sprachen registriert, da sich die Sprache des Editors nachträglich ändern kann; der Provider bedient nur das Modell dieses Editors
    private registerQuickFixProvider(): void {
        this.listeners.push(this.monaco.languages.registerCodeActionProvider("*", {
            provideCodeActions: (model, _range, context) => {
                if (model !== this.editor?.getModel()) {
                    return {actions: [], dispose: () => null};
                }
                let actions = context.markers.flatMap(marker => {
                    let fix = this.quickFixes.find(f => f.line == marker.startLineNumber && f.column == marker.startColumn && f.message == marker.message);
                    let range = fix == null ? null : this.getQuickFixRange(model, fix);
                    return range == null ? [] : [{
                        title: fix.title,
                        kind: "quickfix",
                        diagnostics: [marker],
                        isPreferred: true,
                        edit: {
                            edits: [{
                                resource: model.uri,
                                versionId: model.getVersionId(),
                                textEdit: {range, text: fix.newText.replace(/\n/g, model.getEOL())}
                            }]
                        }
                    }];
                });
                return {actions, dispose: () => null};
            }
        }, {providedCodeActionKinds: ["quickfix"]}));
    }

    private getQuickFixRange(model: MonacoModel, fix: EditorQuickFix): MonacoRange {
        let oldText = fix.oldText.replace(/\n/g, model.getEOL());
        let end = model.getPositionAt(model.getOffsetAt({lineNumber: fix.line, column: fix.column}) + oldText.length);
        let range = new this.monaco.Range(fix.line, fix.column, end.lineNumber, end.column);
        return model.getValueInRange(range) == oldText ? range : null;
    }

    // Mit eigener Validierung sind allein deren Meldungen maßgeblich; die eingebaute Sprachprüfung würde z. B. JSON-Fehler doppelt melden
    private suppressLanguageMarkers(uris: readonly import("monaco-editor/esm/vs/editor/editor.api.js").Uri[]): void {
        let model = this.editor?.getModel();
        if (model == null || this.validateCallback == null || !uris.some(uri => uri.toString() == model.uri.toString())) {
            return;
        }
        let language = model.getLanguageId();
        if (this.monaco.editor.getModelMarkers({resource: model.uri, owner: language}).length > 0) {
            this.monaco.editor.setModelMarkers(model, language, []);
        }
    }

    private registerCompletionProvider(): void {
        let provider = this.completionProvider;
        if (provider == null) {
            return;
        }
        // Monaco registriert Provider je Sprache global; Vorschläge gibt es daher nur für das Modell dieses Editors
        this.listeners.push(this.monaco.languages.registerCompletionItemProvider(this.loader.normalizeLanguage(this.language), {
            triggerCharacters: provider.triggerCharacters,
            provideCompletionItems: async (model, position) => {
                if (model !== this.editor?.getModel()) {
                    return {suggestions: []};
                }
                let result = await provider.provide(model.getValue(), model.getOffsetAt(position));
                if (result == null || result.items.length == 0) {
                    return {suggestions: []};
                }
                let start = model.getPositionAt(result.replaceStart);
                let end = model.getPositionAt(result.replaceEnd);
                let range = new this.monaco.Range(start.lineNumber, start.column, end.lineNumber, end.column);
                return {
                    suggestions: result.items.map(item => ({
                        label: item.label,
                        kind: this.monaco.languages.CompletionItemKind.Property,
                        insertText: item.insertText,
                        ...item.detail != null ? {detail: item.detail} : {},
                        ...item.documentation != null ? {documentation: item.documentation} : {},
                        range
                    }))
                };
            }
        }));
    }

    private registerLogFolding(): void {
        if (this.lineBlockFoldingStartPattern == null || MonacoEditorComponent.logFoldingRegistered) {
            return;
        }

        this.monaco.languages.register({id: "log-entries"});
        this.monaco.languages.registerFoldingRangeProvider("log-entries", {
            provideFoldingRanges: model => {
                let pattern = MonacoEditorComponent.logPatterns.get(model);
                if (pattern == null) {
                    return [];
                }

                let starts: number[] = [];
                for (let line = 1; line <= model.getLineCount(); line++) {
                    if (pattern.test(model.getLineContent(line))) {
                        starts.push(line);
                    }
                }
                return starts.flatMap((start, index) => {
                    let end = (starts[index + 1] ?? model.getLineCount() + 1) - 1;
                    return end > start ? [{start, end}] : [];
                });
            }
        });
        MonacoEditorComponent.logFoldingRegistered = true;
    }

    private configureLogFolding(): void {
        let model = this.editor?.getModel();
        if (model == null) {
            return;
        }
        if (this.lineBlockFoldingStartPattern) {
            MonacoEditorComponent.logPatterns.set(model, new RegExp(this.lineBlockFoldingStartPattern));
        }
        else {
            MonacoEditorComponent.logPatterns.delete(model);
        }
    }

    private scheduleInitialFold(): void {
        clearTimeout(this.foldTimer);
        if (this.collapseLineBlocks && this.lineBlockFoldingStartPattern && this.editor != null) {
            this.foldTimer = Utils.setTimerOnce(0, () => void this.editor?.getAction("editor.foldAll")?.run());
        }
    }

    private configureTerminal(): void {
        if (!this.asTerminal) {
            return;
        }

        this.editor.addCommand(this.monaco.KeyCode.Enter, () => {
            let value = this.editor.getValue().trim();
            if (value) {
                this.terminalHistory.push(value);
                this.terminalHistoryIndex = this.terminalHistory.length;
                this.zone.run(() => this.terminalCommand.emit(value));
                this.editor.setValue("");
            }
        });
        this.editor.addCommand(this.monaco.KeyCode.UpArrow, () => {
            if (this.terminalHistory.length > 0) {
                this.terminalHistoryIndex = Math.max(0, this.terminalHistoryIndex - 1);
                this.setTerminalValue(this.terminalHistory[this.terminalHistoryIndex]);
            }
        });
        this.editor.addCommand(this.monaco.KeyCode.DownArrow, () => {
            if (this.terminalHistory.length > 0) {
                this.terminalHistoryIndex = Math.min(this.terminalHistory.length, this.terminalHistoryIndex + 1);
                this.setTerminalValue(this.terminalHistory[this.terminalHistoryIndex] ?? "");
            }
        });
    }

    private setTerminalValue(value: string): void {
        this.editor.setValue(value);
        this.editor.setPosition({lineNumber: 1, column: value.length + 1});
    }

    private async updateLanguage(): Promise<void> {
        let language = this.loader.normalizeLanguage(this.language);
        if (this.monaco == null) {
            this.monaco = await this.loader.load(language);
        }
        else {
            await this.loader.load(language);
        }
        if (!this.destroyed && this.editor?.getModel() != null) {
            this.monaco.editor.setModelLanguage(this.editor.getModel(), language);
            this.scheduleValidation();
        }
    }

    private updateReadOnly(): void {
        this.editor?.updateOptions({readOnly: this.readOnly || this.disabled});
    }
}
